// src/pages/ManagerAssessmentPage.js

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";   // custom auth hook
import "../styles/AssesmentPage.css";
import AssessmentDrawer from "../components/drawer/AssessmentDrawer";
import EmployeeHeader from '../components/EmployeeHeader';
import generateAssessmentPDF from "../components/pdf/ManagerAssessmentPDF";

// Excel export libs
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ManagerAssessmentPage = () => {
  const navigate = useNavigate();
  const { authFetch } = useAuth("/login");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [radiologistList, setRadiologistList] = useState([]);
  const [submodalities, setSubmodalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusCounts, setStatusCounts] = useState({
  pending: 0,
  approved: 0,
  rejected: 0
});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Ref for table if needed
  const tableRef = useRef(null);

  const [currentEmployee, setCurrentEmployee] = useState({
            photo: 'https://randomuser.me/api/portraits/men/32.jpg',
            name: '',
            id: '',
            role: ''
          });
        
          const [showPopup, setShowPopup] = useState(false);
        
          const popupRef = useRef(null);
          const buttonRef = useRef(null);
        
          useEffect(() => {
            const userCode = sessionStorage.getItem('user_code');
            const userName = sessionStorage.getItem('user_name');
            const userRole = sessionStorage.getItem('user_role');
            setCurrentEmployee((prev) => ({
              ...prev,
              name: userName,
              id: userCode,
              role: userRole || 'User'
            }));
          }, []);

  // Fetch Radiologists
  const fetchRadiologistList = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${baseUrl}/getRadiologyCandidates`);
      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        setRadiologistList(data.data);
      } else {
        setRadiologistList([]);
      }
    } catch (err) {
      console.error("Error fetching radiologist list:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
  try {
    const res = await authFetch(`${baseUrl}/getTotalAccountStatus`);
    const data = await res.json();

    if (data.status && data.data) {
      setStatusCounts({
        pending: data.data.pending || 0,
        approved: data.data.approved || 0,
        rejected: data.data.rejected || 0
      });
    }
  } catch (error) {
    console.error("Error fetching status counts:", error);
  }
};

  // Fetch Submodalities (if you're really using them, otherwise can remove)
  const fetchSubmodalities = async () => {
    try {
      const res = await authFetch(`${baseUrl}/getSubModalities`);
      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        const formattedSubs = data.data.map((item) => ({
          modality: item.mod_name,
          classification: item.sub_mod_class,
          submodality: item.sub_mod_name,
          service_id: item.service_id,
        }));
        setSubmodalities(formattedSubs);
      }
    } catch (err) {
      console.error("Error fetching submodalities:", err);
    }
  };

  // Generate PDF
  const handleGeneratePDF = async (candidateId, mode = "view") => {
    try {
      const res = await authFetch(
        `${baseUrl}/getCandidateFullDetails/${candidateId}`
      );
      const result = await res.json();

      if (result && result.candidate) {
        generateAssessmentPDF(result, mode);
      } else {
        alert("Failed to fetch candidate details");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Something went wrong while generating PDF");
    }
  };

  // Edit
  const handleEditClick = (id) => {
    navigate(`/radiologist/manager_edit/${id}`);
  };

  const handleViewClick = (id) => {
    navigate(`/radiologist/view/${id}`);
  };

  useEffect(() => {
    fetchRadiologistList();
    fetchSubmodalities();
    fetchStatusCounts();
  }, []);

  // Filtering
  const filteredData = radiologistList.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // Sorting
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = a[sortConfig.key] || "";
    const valB = b[sortConfig.key] || "";
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  if (loading) {
    return <div>Loading Radiologist data...</div>;
  }

  // Excel export function
  const exportToExcel = () => {
    // Build worksheet from data you want
    const wsData = [
      ["ID", "Trans Code", "Candidate Name", "Assessment Date", "Status"]
      // you can add more headers if columns more
    ];
    // Add rows
    paginatedData.forEach((item, idx) => {
      wsData.push([
        (currentPage - 1) * itemsPerPage + idx + 1,
        item.trans_code,
        item.candidate_name,
        item.created_on,
        item.rpt_mgr_status
      ]);
    });
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Radiologists");
    // Write buffer
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    // Save
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "Radiologist_List.xlsx");
  };

  return (
    <><EmployeeHeader
        currentEmployee={currentEmployee}
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        popupRef={popupRef}
        buttonRef={buttonRef}
      />
    <div className="assessment-page">
      <div className="assessment-header" 
  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
>
  <h2>
    Radiologist List (Manager Screen)
    <span style={{ 
        marginLeft: "15px", 
        fontSize: "14px",
        fontWeight: "normal"
    }}>
      <span style={{ 
        background: "orange", 
        color: "#fff", 
        padding: "2px 6px", 
        borderRadius: "4px",
        marginRight: "8px"
      }}>
        Pending: {statusCounts.pending}
      </span>

      <span style={{ 
        background: "green", 
        color: "#fff", 
        padding: "2px 6px", 
        borderRadius: "4px",
        marginRight: "8px"
      }}>
        Approved: {statusCounts.approved}
      </span>

      <span style={{ 
        background: "red", 
        color: "#fff", 
        padding: "2px 6px", 
        borderRadius: "4px"
      }}>
        Rejected: {statusCounts.rejected}
      </span>
    </span>
  </h2>
</div>


      <div className="assessment-table-container" style={{ padding: "10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <input
            type="text"
            placeholder="Search..."
            className="form-control"
            style={{ width: "250px" }}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          <button
            style={{
              padding: "8px 12px",
              backgroundColor: "#2ca146ff",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={exportToExcel}
          >
            Export to Excel
          </button>
        </div>

        <table
          ref={tableRef}
          className="assessment-table"
          style={{ borderCollapse: "collapse", fontSize: "13px", width: "100%" }}
        >
          <thead>
            <tr style={{ height: "35px" }}>
              <th style={{ padding: "6px" }} onClick={() => requestSort("id")}>ID</th>
              <th style={{ padding: "6px" }} onClick={() => requestSort("candidate_name")}>Candidate</th>
              <th style={{ padding: "6px" }} onClick={() => requestSort("trans_code")}>Trans Code</th>
              <th style={{ padding: "6px" }} onClick={() => requestSort("created_on")}>Assessment Date</th>
              <th style={{ padding: "6px" }} onClick={() => requestSort("rpt_mgr_status")}>Status</th>
              <th style={{ padding: "6px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr key={item.id} style={{ height: "35px" }}>
                  <td style={{ padding: "6px" }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td style={{ padding: "6px" }}>{item.candidate_name}</td>
                  <td style={{ padding: "6px" }}>{item.trans_code}</td>
                  <td style={{ padding: "6px" }}>{item.created_on}</td>
                  <td style={{ padding: "6px" }}>
                    <span
                      style={{
                        backgroundColor:
                          item.rpt_mgr_status === "Pending"
                            ? "orange"
                            : item.rpt_mgr_status === "Approved"
                              ? "green"
                              : item.rpt_mgr_status === "Rejected"
                                ? "red"
                                : "transparent",
                        color:
                          item.rpt_mgr_status === "Approved" ||
                            item.rpt_mgr_status === "Rejected" ||
                            item.rpt_mgr_status === "Pending"
                            ? "white"
                            : "black",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: "bold",
                        display: "inline-block",
                        minWidth: "80px",
                        textAlign: "center",
                        fontSize: "12px",
                      }}
                    >
                      {item.rpt_mgr_status}
                    </span>
                  </td>
                  <td style={{ padding: "6px" }}>
                    
                      <button
                        className="action-button view"
                        onClick={() => handleViewClick(item.id, "view")}
                      >
                        View
                      </button>
                   
                    {(item.rpt_mgr_status === "Pending" || item.rpt_mgr_status === "Rejected") && (
                    <button
                      className="action-button edit"
                      onClick={() => handleEditClick(item.id)}
                    >
                      Edit
                    </button>)}
                    
                      <button
                        className="action-button download"
                        onClick={() => handleGeneratePDF(item.id, "download")}
                      >
                        Download
                      </button>
                   
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: "6px" }}>
                  No Radiologist data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Show count of items */}
        {/* Entries + Pagination aligned */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: "14px" }}>
            Showing {paginatedData.length} of {totalItems} entries
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #ccc",
                  cursor: "pointer",
                  backgroundColor: "#f7f7f7",
                }}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: "6px 12px",
                    fontWeight: currentPage === page ? "bold" : "normal",
                    backgroundColor: currentPage === page ? "#007bff" : "#fff",
                    color: currentPage === page ? "#fff" : "#000",
                    border: "1px solid #ccc",
                    cursor: "pointer",
                  }}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #ccc",
                  cursor: "pointer",
                  backgroundColor: "#f7f7f7",
                }}
              >
                &gt;
              </button>
            </div>
          )}
        </div>

      </div>

      {isDrawerOpen && (
        <AssessmentDrawer onClose={() => setIsDrawerOpen(false)} />
      )}
    </div>
    </>
  );
};

export default ManagerAssessmentPage;
